import { useState } from "react";
import { Download, Pencil, Plus, Trash2 } from "lucide-react";

import {
  RowActionsMenu,
  type RowAction,
} from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/core/components/ui/table";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useDeletePayrollLine } from "@/features/payroll/api/use-delete-payroll-line";
import { useDownloadPayrollLineStatement } from "@/features/payroll/api/use-download-payroll-line-statement";
import type {
  PayrollBatchDto,
  PayrollBatchLineDto,
} from "@/features/payroll/api/dto";
import type { PayrollLineStatus } from "@/features/payroll/api/enums";
import { PayrollLineFormDialog } from "@/features/payroll/components/batch-detail/payroll-line-form-dialog";
import { AmountMinor } from "@/features/shared/components/amount-minor";

const LINE_STATUS_VARIANT: Record<
  PayrollLineStatus,
  "default" | "secondary" | "success" | "warning" | "destructive"
> = {
  PENDING: "secondary",
  VALID: "default",
  INVALID: "destructive",
  PROCESSING: "warning",
  PAID: "success",
  FAILED: "destructive",
  REJECTED: "destructive",
};

/**
 * Section « Lignes de la paie » — tableau des salariés avec statut par ligne,
 * ajout, édition inline et téléchargement du relevé individuel pour les lignes
 * `PAID`. Les actions édition / suppression ne sont visibles que si la paie
 * est modifiable (`DRAFT` ou `SIMULATED`).
 */
export function PayrollLinesSection({ batch }: { batch: PayrollBatchDto }) {
  const { t } = useTypedTranslation();
  const tPayroll = (key: string) => t(key as TFunctionType);
  const canEdit = batch.status === "DRAFT" || batch.status === "SIMULATED";

  const [addOpen, setAddOpen] = useState(false);
  const [editLine, setEditLine] = useState<PayrollBatchLineDto | null>(null);
  const [deleteLine, setDeleteLine] = useState<PayrollBatchLineDto | null>(null);

  const deleteMutation = useDeletePayrollLine(batch.id);
  const downloadLine = useDownloadPayrollLineStatement();

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base">
            {t("payroll.lines.section.title")} ·{" "}
            <NoTranslate as="span">{batch.lines.length}</NoTranslate>
          </CardTitle>
          {canEdit && (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t("payroll.lines.add.cta")}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {batch.lines.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("payroll.lines.section.empty")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("payroll.lines.columns.name")}</TableHead>
                  <TableHead>{t("payroll.lines.columns.reference")}</TableHead>
                  <TableHead>{t("payroll.lines.columns.phone")}</TableHead>
                  <TableHead>{t("payroll.lines.columns.amount")}</TableHead>
                  <TableHead>{t("payroll.lines.columns.net")}</TableHead>
                  <TableHead>{t("payroll.lines.columns.status")}</TableHead>
                  <TableHead className="w-10 text-right">
                    {t("common.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batch.lines.map((line) => {
                  const actions: RowAction[] = [];
                  if (canEdit) {
                    actions.push(
                      {
                        id: "edit",
                        label: t("payroll.lines.actions.edit"),
                        icon: Pencil,
                        onClick: () => setEditLine(line),
                      },
                      {
                        id: "delete",
                        label: t("payroll.lines.actions.delete"),
                        icon: Trash2,
                        variant: "destructive",
                        onClick: () => setDeleteLine(line),
                      }
                    );
                  }
                  if (line.status === "PAID") {
                    actions.push({
                      id: "download",
                      label: t("payroll.lines.actions.download"),
                      icon: Download,
                      onClick: () =>
                        downloadLine.mutate(
                          { batchId: batch.id, lineId: line.id },
                          {
                            onError: (error) =>
                              notifSilenceError(
                                apiErrorHandler(error) || error.message
                              ),
                          }
                        ),
                    });
                  }

                  return (
                    <TableRow key={line.id}>
                      <TableCell>
                        <NoTranslate as="span">{line.employeeName}</NoTranslate>
                      </TableCell>
                      <TableCell>
                        <NoTranslate as="span" className="text-xs">
                          {line.employeeReference || "—"}
                        </NoTranslate>
                      </TableCell>
                      <TableCell>
                        <NoTranslate as="span" className="font-mono text-xs">
                          {line.phone}
                        </NoTranslate>
                      </TableCell>
                      <TableCell>
                        <AmountMinor
                          amountMinor={line.amountMinor}
                          currency={batch.currency}
                        />
                      </TableCell>
                      <TableCell>
                        {line.netAmountMinor != null ? (
                          <AmountMinor
                            amountMinor={line.netAmountMinor}
                            currency={batch.currency}
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            LINE_STATUS_VARIANT[line.status] ?? "outline"
                          }
                        >
                          {t(`payroll.enums.lineStatus.${line.status}`)}
                        </Badge>
                        {line.invalidReason && (
                          <p className="mt-1 text-xs text-destructive">
                            {line.invalidReason}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {actions.length > 0 ? (
                          <RowActionsMenu actions={actions} />
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {downloadLine.isPending && (
            <p className="mt-2 inline-flex items-center gap-2 text-xs text-muted-foreground">
              <ButtonSpinner size="sm" />
              {t("payroll.lines.feedback.downloading")}
            </p>
          )}
        </CardContent>
      </Card>

      {addOpen && (
        <PayrollLineFormDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          batchId={batch.id}
        />
      )}
      {editLine && (
        <PayrollLineFormDialog
          open={editLine !== null}
          onOpenChange={(isOpen) => {
            if (!isOpen) setEditLine(null);
          }}
          batchId={batch.id}
          line={editLine}
        />
      )}

      <MutationConfirmPreview
        open={deleteLine !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setDeleteLine(null);
        }}
        data={
          deleteLine && {
            employeeName: deleteLine.employeeName,
            phone: deleteLine.phone,
            amountMinor: deleteLine.amountMinor,
          }
        }
        title={t("payroll.lines.delete.previewTitle")}
        description={t("payroll.lines.delete.previewDescription")}
        confirmLabel={t("payroll.lines.actions.delete")}
        t={tPayroll}
        pending={deleteMutation.isPending}
        onConfirm={() => {
          if (!deleteLine) return;
          deleteMutation.mutate(deleteLine.id, {
            onSuccess: () => {
              notifSilence(t("payroll.lines.feedback.deleted"));
              setDeleteLine(null);
            },
            onError: (error) =>
              notifSilenceError(apiErrorHandler(error) || error.message),
          });
        }}
      />
    </>
  );
}
